import React, { useState, useEffect } from 'react';
import { matchesApi, clubsApi, championshipsApi, eaApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  Shield,
  Trophy,
  Calendar,
  Lock,
  Unlock,
  Eye,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Zap,
} from 'lucide-react';

export const ReportsTab = ({ isItalian }) => {
  const [matches, setMatches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsLocked, setReportsLocked] = useState(false);
  const [togglingLock, setTogglingLock] = useState(false);
  const [selectedChamp, setSelectedChamp] = useState('');
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [autoReport, setAutoReport] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [runningAuto, setRunningAuto] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mRes, cRes, chRes, lockRes] = await Promise.all([
        matchesApi.list(),
        clubsApi.list(),
        championshipsApi.list(),
        matchesApi.getReportsLock(),
      ]);
      setMatches(mRes.data || []);
      setClubs(cRes.data || []);
      setChampionships(chRes.data || []);
      setReportsLocked(lockRes.data?.locked || false);
    } catch (e) {
      console.error('Error loading reports data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    eaApi.getAutoReport().then(r => setAutoReport(!!r.data?.enabled)).catch(() => {});
  }, []);

  const handleToggleAutoReport = async () => {
    setTogglingAuto(true);
    try {
      const res = await eaApi.toggleAutoReport(!autoReport);
      setAutoReport(!!res.data?.enabled);
      toast.success(res.data?.message || 'OK');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally {
      setTogglingAuto(false);
    }
  };

  const handleRunAutoReport = async () => {
    setRunningAuto(true);
    try {
      const res = await eaApi.runAutoReport();
      toast.success(res.data?.message || 'OK');
      if (res.data?.completed > 0) await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isItalian ? 'Errore report automatico' : 'Auto report error'));
    } finally {
      setRunningAuto(false);
    }
  };

  const getClub = (id) => clubs.find(c => c.club_id === id);
  const getChamp = (id) => championships.find(c => c.championship_id === id);

  const handleToggleLock = async () => {
    setTogglingLock(true);
    try {
      const res = await matchesApi.toggleReportsLock();
      setReportsLocked(res.data?.locked || false);
      toast.success(isItalian
        ? `Report ${res.data?.locked ? 'bloccati' : 'sbloccati'}`
        : `Reports ${res.data?.locked ? 'locked' : 'unlocked'}`);
    } catch (e) {
      toast.error(isItalian ? 'Errore' : 'Error');
    } finally {
      setTogglingLock(false);
    }
  };

  const handleResolveDispute = async (matchId, homeScore, awayScore) => {
    setResolving(matchId);
    try {
      await matchesApi.resolveDispute(matchId, { home_score: homeScore, away_score: awayScore, accepted_report_index: null });
      toast.success(isItalian ? 'Contestazione risolta!' : 'Dispute resolved!');
      await fetchData();
      setExpandedMatch(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally {
      setResolving(null);
    }
  };

  const handleCancelReport = async (matchId) => {
    if (!window.confirm(isItalian ? 'Annullare questo report?' : 'Cancel this report?')) return;
    try {
      await matchesApi.cancelReport(matchId);
      toast.success(isItalian ? 'Report annullato' : 'Report cancelled');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleResyncMatch = async (matchId) => {
    try {
      const res = await matchesApi.resync(matchId);
      toast.success(res.data?.message || (isItalian ? 'Stato ricalcolato' : 'Status resynced'));
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleResyncAll = async () => {
    if (!window.confirm(isItalian
      ? 'Forza il ricontrollo di TUTTE le partite in attesa con 2 report? Quelle con report coincidenti verranno completate automaticamente.'
      : 'Force re-check of ALL pending matches with 2 reports? Matching ones will auto-complete.')) return;
    try {
      const res = await matchesApi.resyncAllPending();
      const d = res.data || {};
      toast.success(isItalian
        ? `Completate: ${d.completed || 0}, Contestate: ${d.disputed || 0}, Invariate: ${d.unchanged || 0}`
        : `Completed: ${d.completed || 0}, Disputed: ${d.disputed || 0}, Unchanged: ${d.unchanged || 0}`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  // Filter matches that have report activity
  let filtered = matches;
  if (selectedChamp) filtered = filtered.filter(m => m.championship_id === selectedChamp);

  const pending = filtered.filter(m => m.status === 'pending_report');
  const disputed = filtered.filter(m => m.status === 'disputed');
  const completed = filtered.filter(m => m.status === 'completed').sort((a, b) => {
    const da = a.completed_at || a.match_date || '';
    const db = b.completed_at || b.match_date || '';
    return db.localeCompare(da);
  });
  const scheduled = filtered.filter(m => m.status === 'scheduled');

  const getDisplayList = () => {
    if (statusFilter === 'disputed') return disputed;
    if (statusFilter === 'pending') return pending;
    if (statusFilter === 'completed') return completed.slice(0, 50);
    if (statusFilter === 'scheduled') return scheduled;
    return [...disputed, ...pending, ...completed.slice(0, 30)];
  };

  const displayList = getDisplayList();

  const StatusBadge = ({ status, forfeit }) => {
    const cfg = {
      disputed: { text: isItalian ? 'Contestata' : 'Disputed', cls: 'bg-[#FF1744]/15 text-[#FF1744] border-[#FF1744]/30', icon: AlertTriangle },
      pending_report: { text: isItalian ? '1 Report' : '1 Report', cls: 'bg-[#FF9100]/15 text-[#FF9100] border-[#FF9100]/30', icon: Clock },
      completed: { text: forfeit ? 'Tavolino' : (isItalian ? 'Completata' : 'Completed'), cls: forfeit ? 'bg-orange-500/15 text-orange-400 border-orange-500/30' : 'bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30', icon: CheckCircle },
      scheduled: { text: isItalian ? 'Programmata' : 'Scheduled', cls: 'bg-[#FFEA00]/15 text-[#FFEA00] border-[#FFEA00]/30', icon: Calendar },
    };
    const c = cfg[status] || cfg.scheduled;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${c.cls}`}>
        <Icon size={10} /> {c.text}
      </span>
    );
  };

  const MatchReportRow = ({ match }) => {
    const home = getClub(match.home_club_id);
    const away = getClub(match.away_club_id);
    const champ = getChamp(match.championship_id);
    const reports = match.reports || [];
    const isExpanded = expandedMatch === match.match_id;

    return (
      <div className="bg-[#0a0d15] border border-white/10 hover:border-white/20 transition-colors" data-testid={`report-row-${match.match_id}`}>
        <div
          className="p-3 flex items-center gap-3 cursor-pointer"
          onClick={() => setExpandedMatch(isExpanded ? null : match.match_id)}
        >
          <StatusBadge status={match.status} forfeit={match.forfeit} />

          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              {home?.logo ? (
                <img src={home.logo} alt="" className="w-5 h-5 object-cover rounded-sm flex-shrink-0" />
              ) : (
                <Shield size={14} className="text-slate-600 flex-shrink-0" />
              )}
              <span className="text-sm text-white truncate">{home?.name || '?'}</span>
            </div>

            {match.status === 'completed' ? (
              <span className="font-mono text-sm font-bold text-white flex-shrink-0 px-2">
                {match.home_score} - {match.away_score}
              </span>
            ) : (
              <span className="text-xs text-slate-600 flex-shrink-0 px-2">vs</span>
            )}

            <div className="flex items-center gap-1.5 min-w-0">
              {away?.logo ? (
                <img src={away.logo} alt="" className="w-5 h-5 object-cover rounded-sm flex-shrink-0" />
              ) : (
                <Shield size={14} className="text-slate-600 flex-shrink-0" />
              )}
              <span className="text-sm text-white truncate">{away?.name || '?'}</span>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            {match.group && (
              <span className="text-[10px] text-[#7C4DFF] font-bold uppercase">G.{match.group}</span>
            )}
            {champ && (
              <span className="text-[10px] text-slate-600 truncate max-w-[100px]">{champ.name}</span>
            )}
            {match.match_day && (
              <span className="text-[10px] text-slate-600">GG{match.match_day}</span>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {reports.length > 0 && (
              <span className="text-[10px] text-slate-500">{reports.length}/2</span>
            )}
            {isExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-white/5 p-3 space-y-3 bg-[#050508]">
            {/* Report details */}
            {reports.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {reports.map((r, idx) => {
                  const rClub = getClub(r.club_id);
                  return (
                    <div key={idx} className="bg-[#0a0d15] border border-white/10 rounded p-3" data-testid={`report-detail-${match.match_id}-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {rClub?.logo ? (
                          <img src={rClub.logo} alt="" className="w-4 h-4 object-cover rounded-sm" />
                        ) : (
                          <Shield size={12} className="text-slate-600" />
                        )}
                        <span className="text-xs font-bold text-white">{rClub?.name || '?'}</span>
                        <span className="text-[10px] text-slate-500">Report #{idx + 1}</span>
                      </div>
                      <p className="font-mono text-lg font-bold text-white text-center mb-2">
                        {r.home_score} - {r.away_score}
                      </p>
                      {r.events && r.events.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {r.events.map((ev, eidx) => (
                            <span key={eidx} className={`px-1.5 py-0.5 text-[10px] rounded ${
                              ev.event_type === 'goal' ? 'bg-[#00E676]/15 text-[#00E676]'
                              : ev.event_type === 'assist' ? 'bg-[#00F0FF]/15 text-[#00F0FF]'
                              : ev.event_type === 'yellow_card' ? 'bg-[#FFEA00]/15 text-[#FFEA00]'
                              : 'bg-[#FF1744]/15 text-[#FF1744]'
                            }`}>
                              {ev.player_name || '?'} {ev.minute}'
                            </span>
                          ))}
                        </div>
                      )}
                      {r.submitted_at && (
                        <p className="text-[10px] text-slate-600 mt-2">
                          {new Date(r.submitted_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-600 text-center py-2">
                {isItalian ? 'Nessun report inviato' : 'No reports submitted'}
              </p>
            )}

            {/* Dispute resolution buttons */}
            {match.status === 'disputed' && reports.length === 2 && (
              <div className="space-y-2">
                <p className="text-xs text-[#FF1744] font-bold uppercase tracking-wider">
                  {isItalian ? 'Risolvi Contestazione' : 'Resolve Dispute'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {reports.map((r, idx) => {
                    const rClub = getClub(r.club_id);
                    return (
                      <Button
                        key={idx}
                        size="sm"
                        className="bg-white/5 border border-white/10 text-white hover:bg-[#00F0FF]/10 hover:border-[#00F0FF]/30"
                        onClick={() => handleResolveDispute(match.match_id, r.home_score, r.away_score)}
                        disabled={resolving === match.match_id}
                        data-testid={`accept-report-${match.match_id}-${idx}`}
                      >
                        {isItalian ? 'Accetta' : 'Accept'} {rClub?.name}: {r.home_score}-{r.away_score}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              {match.status === 'pending_report' && (match.reports || []).length >= 2 && (
                <Button
                  size="sm"
                  className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20"
                  onClick={() => handleResyncMatch(match.match_id)}
                  data-testid={`resync-match-${match.match_id}`}
                >
                  <Zap size={14} className="mr-1" />
                  {isItalian ? 'Forza Verifica' : 'Force Resync'}
                </Button>
              )}
              {(match.status === 'pending_report' || match.status === 'disputed') && (
                <Button
                  size="sm"
                  className="bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] hover:bg-[#FF1744]/20"
                  onClick={() => handleCancelReport(match.match_id)}
                  data-testid={`cancel-report-btn-${match.match_id}`}
                >
                  {isItalian ? 'Annulla Report' : 'Cancel Report'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin w-6 h-6 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reports-tab">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[#00F0FF]" />
          <h2 className="text-white font-bold uppercase tracking-wider text-sm">
            {isItalian ? 'Gestione Report' : 'Report Management'}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            className={`${autoReport
              ? 'bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/25'
              : 'bg-white/5 border border-white/15 text-slate-400 hover:bg-white/10'
            }`}
            onClick={handleToggleAutoReport}
            disabled={togglingAuto}
            data-testid="toggle-auto-report"
            title={isItalian
              ? 'Se attivo, le partite giocate vengono lette da EA e i report si compilano da soli (controllo ogni 30 minuti)'
              : 'If on, played matches are read from EA and reports auto-fill (checked every 30 minutes)'}
          >
            <Zap size={14} className="mr-1.5" />
            {isItalian
              ? `Report Auto EA: ${autoReport ? 'ON' : 'OFF'}`
              : `EA Auto Report: ${autoReport ? 'ON' : 'OFF'}`}
          </Button>
          {autoReport && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#00F0FF]/30 text-[#00F0FF]"
              onClick={handleRunAutoReport}
              disabled={runningAuto}
              data-testid="run-auto-report"
            >
              <RefreshCw size={14} className={`mr-1.5 ${runningAuto ? 'animate-spin' : ''}`} />
              {isItalian ? 'Esegui Ora' : 'Run Now'}
            </Button>
          )}
          <Button
            size="sm"
            className={`${reportsLocked
              ? 'bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] hover:bg-[#FF1744]/20'
              : 'bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/20'
            }`}
            onClick={handleToggleLock}
            disabled={togglingLock}
            data-testid="toggle-reports-lock"
          >
            {reportsLocked ? <Lock size={14} className="mr-1.5" /> : <Unlock size={14} className="mr-1.5" />}
            {reportsLocked
              ? (isItalian ? 'Report Bloccati' : 'Reports Locked')
              : (isItalian ? 'Report Aperti' : 'Reports Open')}
          </Button>
          <Button size="sm" variant="outline" className="border-white/10" onClick={fetchData} data-testid="refresh-reports">
            <RefreshCw size={14} className="mr-1.5" />
            {isItalian ? 'Aggiorna' : 'Refresh'}
          </Button>
          <Button
            size="sm"
            className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20"
            onClick={handleResyncAll}
            data-testid="resync-all-pending"
            title={isItalian ? 'Verifica tutte le partite in attesa e auto-completa quelle con report coincidenti' : 'Check all pending matches and auto-complete matching ones'}
          >
            <Zap size={14} className="mr-1.5" />
            {isItalian ? 'Sblocca In Attesa' : 'Unstick Pending'}
          </Button>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: isItalian ? 'Contestate' : 'Disputed', count: disputed.length, color: 'text-[#FF1744]', bg: 'bg-[#FF1744]/10 border-[#FF1744]/20', icon: AlertTriangle, filter: 'disputed' },
          { label: isItalian ? 'In Attesa' : 'Pending', count: pending.length, color: 'text-[#FF9100]', bg: 'bg-[#FF9100]/10 border-[#FF9100]/20', icon: Clock, filter: 'pending' },
          { label: isItalian ? 'Completate' : 'Completed', count: completed.length, color: 'text-[#00E676]', bg: 'bg-[#00E676]/10 border-[#00E676]/20', icon: CheckCircle, filter: 'completed' },
          { label: isItalian ? 'Programmate' : 'Scheduled', count: scheduled.length, color: 'text-[#FFEA00]', bg: 'bg-[#FFEA00]/10 border-[#FFEA00]/20', icon: Calendar, filter: 'scheduled' },
        ].map((s) => {
          const Icon = s.icon;
          const isActive = statusFilter === s.filter;
          return (
            <button
              key={s.filter}
              onClick={() => setStatusFilter(isActive ? 'all' : s.filter)}
              className={`p-3 border rounded text-left transition-all ${isActive ? s.bg + ' border-current' : 'bg-[#0a0d15] border-white/10 hover:border-white/20'}`}
              data-testid={`filter-${s.filter}`}
            >
              <div className="flex items-center gap-2">
                <Icon size={16} className={s.color} />
                <span className={`text-xl font-bold font-mono ${s.color}`}>{s.count}</span>
              </div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{s.label}</p>
            </button>
          );
        })}
      </div>

      {/* Championship filter */}
      <div className="flex items-center gap-2">
        <Trophy size={14} className="text-[#FFEA00] flex-shrink-0" />
        <select
          value={selectedChamp}
          onChange={(e) => setSelectedChamp(e.target.value)}
          className="bg-[#0a0d15] border border-white/10 text-white px-3 py-1.5 text-sm rounded w-full sm:w-auto"
          data-testid="reports-champ-filter"
        >
          <option value="">{isItalian ? 'Tutti i campionati' : 'All championships'}</option>
          {championships.filter(c => c.status === 'in_progress' || c.status === 'completed').map(c => (
            <option key={c.championship_id} value={c.championship_id}>{c.name}</option>
          ))}
        </select>
        {statusFilter !== 'all' && (
          <button
            onClick={() => setStatusFilter('all')}
            className="text-xs text-[#00F0FF] hover:underline"
            data-testid="clear-status-filter"
          >
            {isItalian ? 'Mostra tutti' : 'Show all'}
          </button>
        )}
      </div>

      {/* Match list */}
      <div className="space-y-1">
        {displayList.length > 0 ? (
          displayList.map(match => (
            <MatchReportRow key={match.match_id} match={match} />
          ))
        ) : (
          <div className="text-center py-8 bg-[#0a0d15] border border-white/10 rounded">
            <FileText size={32} className="mx-auto text-slate-600 mb-3" />
            <p className="text-slate-500 text-sm">
              {isItalian ? 'Nessun report trovato' : 'No reports found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
